<template>
  <div class="page-node-renderer">
    <!-- Top Navigation Bar -->
    <topnavbar
      :testData="props.testData"
      :answers="{}"
      :stop-timer="props.stopTimer"
      @time-up="$emit('time-up')"
    />

    <div class="container wrapper mt-4" :style="containerStyles">
      <!-- Page Title -->
      <h1 v-if="props.testData">{{ props.testData.title }}</h1>
      <p class="test-description">{{ props.testData.Description }}</p>

      <!-- Page Description -->
      <div v-if="pageNode.data.description" class="alert alert-info mb-4">
        <i class="fas fa-info-circle me-2"></i>
        {{ pageNode.data.description }}
      </div>

      <!-- Navigation Mode Info -->
      <div class="navigation-info mb-4">
        <small class="text-muted">
          <i class="fas fa-route me-1"></i>
          <span v-if="navigationMode === 'all-required'">
            All questions on this page must be answered
          </span>
          <span v-else-if="navigationMode === 'any-one'">
            At least one question must be answered
          </span>
          <span v-else>
            You can answer any questions and continue when ready
          </span>
        </small>
      </div>

      <!-- Child Interactions -->
      <div v-if="childElements.length > 0" class="interactions-container">
        <div
          v-for="(child, index) in childElements"
          :key="child.element.el_ID"
          class="interaction-card mb-4"
        >
          <div class="card shadow-sm" :style="cardStyles">
            <div class="card-header bg-light">
              <h5 class="mb-0">
                <i class="fas fa-question-circle me-2"></i>
                Question {{ index + 1 }}
              </h5>
            </div>
            <div class="card-body">
              <!-- Render the interaction component dynamically -->
              <component
                :is="child.component"
                v-bind="child.props"
                @interaction-started="handleInteractionStarted(child.node.id)"
                @interaction-completed="handleInteractionCompleted(child.node.id, $event)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- No Children State -->
      <div v-else class="alert alert-warning">
        ⚠️ This page contains no questions
      </div>

      <!-- Continue Button -->
      <div class="continue-section" :style="continueButtonStyles">
        <button
          class="btn btn-primary btn-lg"
          @click="handleContinue"
          :disabled="!canContinue"
          data-page-continue="continuePage"
        >
          <span v-if="canContinue">
            Continue →
          </span>
          <span v-else>
            <i class="fas fa-lock me-2"></i>
            {{ validationMessage }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref } from 'vue';
import { useResponsesStore } from '@/stores/useResponsesStore';
import topnavbar from './topnavbar.vue';

// Import interaction renders
import ChoiceRender from './interactions/ChoiceRender.vue';
import ChoiceBlockRender from './interactions/ChoiceBlockRender.vue';
import TextGapsRender from './interactions/TextGapsRender.vue';
import ShortAnswerRender from './interactions/ShortAnswerRender.vue';
import OrderRender from './interactions/OrderRender.vue';
import MatchRender from './interactions/MatchRender.vue';
import MakingPairsRender from './interactions/MakingPairsRender.vue';
import HotSpotRender from './interactions/hotSpotRender.vue';
import MessageRender from './interactions/MessageRender.vue';
import TextPuzzleRender from './interactions/TextPuzzleRender.vue';

const responsesStore = useResponsesStore();

const props = defineProps({
  pageNode: {
    type: Object,
    required: true
  },
  childNodes: {
    type: Array,
    required: true
  },
  childElements: {
    type: Array,
    required: true
  },
  testData: {
    type: Object,
    required: true
  },
  onContinue: {
    type: Function,
    required: true
  },
  stopTimer: {
    type: Boolean,
    default: false
  },
  previewMode: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['time-up']);

// Track local completion state for each interaction
const localCompletionState = ref({});

/**
 * Map interaction types to their render components
 */
const interactionComponentsMap = {
  choice: ChoiceRender,
  choiceBlock: ChoiceBlockRender,
  textGaps: TextGapsRender,
  shortAnswer: ShortAnswerRender,
  order: OrderRender,
  match: MatchRender,
  makingPairs: MakingPairsRender,
  hotSpot: HotSpotRender,
  message: MessageRender,
  textPuzzle: TextPuzzleRender
};

/**
 * Container styles based on display settings
 */
const containerStyles = computed(() => {
  const settings = props.testData?.displaySettings;
  console.log('🎨 [PageNodeRenderer] containerStyles computed - settings:', settings);

  if (!settings) {
    console.warn('⚠️ [PageNodeRenderer] No displaySettings found in testData');
    return {};
  }

  const styles = {
    margin: `${settings.containerMargin || 20}px auto`,
    padding: `${settings.containerPadding || 20}px`,
    backgroundColor: settings.containerBackground || '#ffffff',
    maxWidth: settings.maxWidth && settings.maxWidth > 0 ? `${settings.maxWidth}px` : '100%',
    marginLeft: settings.containerAlignment === 'left' ? '0' :
                settings.containerAlignment === 'right' ? 'auto' : 'auto',
    marginRight: settings.containerAlignment === 'right' ? '0' :
                 settings.containerAlignment === 'left' ? 'auto' : 'auto',
    '--question-spacing': `${settings.questionSpacing || 15}px`,
    color: settings.textColor || '#000000'
  };

  console.log('🎨 [PageNodeRenderer] Applied styles:', styles);
  return styles;
});

/**
 * Continue button styles based on display settings
 */
const continueButtonStyles = computed(() => {
  const settings = props.testData?.displaySettings;

  if (!settings) {
    return {
      marginTop: '20px',
      marginBottom: '20px',
      textAlign: 'center'
    };
  }

  return {
    marginTop: `${settings.submitButtonMarginTop || 20}px`,
    marginBottom: `${settings.submitButtonMarginBottom || 20}px`,
    textAlign: settings.submitButtonAlignment || 'center'
  };
});

/**
 * Card styles based on display settings (for pageBackground)
 */
const cardStyles = computed(() => {
  const settings = props.testData?.displaySettings;

  if (!settings) {
    return {};
  }

  return {
    backgroundColor: settings.pageBackground || '#f8f9fa',
    padding: `${settings.pagePadding || 15}px`,
    margin: '0',
    boxSizing: 'border-box',
    width: '100%'
  };
});

/**
 * Get navigation mode from page node
 */
const navigationMode = computed(() => {
  return props.pageNode.data?.navigationMode || 'all-required';
});

/**
 * Prepare child interactions with their components
 */
const childElements = computed(() => {
  return props.childElements.map((item, index) => {
    const element = item.element;
    const node = item.node;
    const interactionType = element.el_Type;

    const component = interactionComponentsMap[interactionType];

    if (!component) {
      console.error('❌ Unknown interaction type:', interactionType);
      return null;
    }

    // Build props for the component
    const componentProps = {
      question: element,
      testData: props.testData,
      questionIndex: index + 1,
      questionIndexMap: {},
      readOnly: false
    };

    return {
      node,
      element,
      component,
      props: componentProps
    };
  }).filter(Boolean);
});

/**
 * Check if a specific interaction has been answered
 */
const isInteractionAnswered = (nodeId, elementId) => {
  // Check local state first (for preview mode)
  if (localCompletionState.value[nodeId]) {
    return true;
  }

  // Check in store
  const response = responsesStore.responses.find(
    r => r.questionId === elementId
  );

  if (!response) return false;

  // Check based on interaction type
  if (response.type === 'choice' || response.type === 'choiceBlock') {
    return response.selectedOptions && response.selectedOptions.length > 0;
  }

  if (response.type === 'textGaps') {
    return response.answered === true;
  }

  if (response.type === 'shortAnswer') {
    return response.selectedOptions && response.selectedOptions.trim() !== '';
  }

  if (response.type === 'order') {
    return response.labelsOrder && response.labelsOrder.length > 0 && response.answered === true;
  }

  if (response.type === 'match') {
    return response.answered === true;
  }

  if (response.type === 'makingPairs') {
    return response.selectedPairs && response.selectedPairs.length > 0;
  }

  if (response.type === 'hotSpot') {
    return response.placedStickersData && response.placedStickersData.length > 0;
  }

  if (response.type === 'textPuzzle') {
    return response.gapAnswers && Object.keys(response.gapAnswers).length > 0;
  }

  if (response.type === 'message') {
    return true;
  }

  return response.answered === true;
};

/**
 * Check if user can continue based on navigation mode
 */
const canContinue = computed(() => {
  const mode = navigationMode.value;

  const answeredCount = childElements.value.filter(child => {
    return isInteractionAnswered(child.node.id, child.element.el_ID);
  }).length;

  const totalCount = childElements.value.length;

  console.log('📊 Page validation:', {
    mode,
    answeredCount,
    totalCount
  });

  if (mode === 'all-required') {
    return answeredCount === totalCount;
  }

  if (mode === 'any-one') {
    return answeredCount >= 1;
  }

  if (mode === 'free') {
    return true;
  }

  return false;
});

/**
 * Validation message to display
 */
const validationMessage = computed(() => {
  const mode = navigationMode.value;
  const answeredCount = childElements.value.filter(child => {
    return isInteractionAnswered(child.node.id, child.element.el_ID);
  }).length;
  const totalCount = childElements.value.length;
  const remainingCount = totalCount - answeredCount;

  if (mode === 'all-required') {
    return `Answer all questions (${remainingCount} remaining)`;
  }

  if (mode === 'any-one') {
    return `Answer at least one question`;
  }

  return 'Continue when ready';
});

/**
 * Handle interaction started event
 */
const handleInteractionStarted = (nodeId) => {
  console.log('🎯 Interaction started on node:', nodeId);
};

/**
 * Handle interaction completed event
 */
const handleInteractionCompleted = (nodeId, data) => {
  console.log('✅ Interaction completed on node:', nodeId, data);
  localCompletionState.value[nodeId] = true;
};

/**
 * Handle continue button click
 */
const handleContinue = () => {
  if (!canContinue.value) {
    console.warn('⚠️ Cannot continue yet');
    return;
  }

  console.log('➡️ Continue to next node');

  // Collect all responses from child nodes
  const childResponses = childElements.value
    .map(child => {
      const response = responsesStore.responses.find(
        r => r.questionId === child.element.el_ID
      );
      return response ? { nodeId: child.node.id, response } : null;
    })
    .filter(Boolean);

  // Call parent handler with collected responses
  props.onContinue(childResponses);
};
</script>

<style scoped>
.page-node-renderer {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.wrapper {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
}

.test-description {
  color: #6c757d;
  margin-bottom: 20px;
}

.navigation-info {
  padding: 10px 15px;
  background-color: #fff;
  border-left: 4px solid #0d6efd;
  border-radius: 4px;
}

.interactions-container {
  margin-top: 20px;
}

.interaction-card {
  transition: transform 0.2s ease;
}

.interaction-card:hover {
  transform: translateY(-2px);
}

.card {
  border: none;
  border-radius: 10px;
}

.card-header {
  border-radius: 10px 10px 0 0 !important;
  border-bottom: 2px solid #dee2e6;
}

.continue-section {
  text-align: center;
  padding: 30px 0;
}

.btn-primary {
  padding: 12px 50px;
  font-size: 1.1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  min-width: 250px;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
